import { useAppDispatch } from 'hooks/redux';
import _ from 'lodash';
import React, { Fragment, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import ChevronIcon from '@mui/icons-material/ChevronRightRounded';
import CheckIcon from '@mui/icons-material/DoneRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import LockIcon from '@mui/icons-material/LockRounded';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { scheduledTransactionsActions, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { getLogger } from 'app-components/utils/core';

import billPresentmentApi from 'data/billPresentment/api';
import { useBillersWithLogos } from 'data/billPresentment/hooks';
import type { Biller, EnrollResponse, Field, Parameter } from 'data/billPresentment/types';

import ConnectFIs from 'assets/illustrations/ConnectFIs.svg';
import Dump from 'components/Dump';

import { StepEnum } from '../types';

const log = getLogger('BillPresentment/LoginPage');

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    margin: '12px 0 72px',
    minHeight: 316,
    maxHeight: '100%',
    flex: 1,
    maxWidth: 464,
    // width: 'calc(100% - 118px)',
  },
  // form styles
  form: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'flex-start',
    // maxHeight: 'calc(100% - 32px)',
    overflow: 'auto',
    paddingBottom: 24,
  },

  // action/footer styles
  formActions: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderTop: `1px solid ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.paper,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  subLock: {
    height: 20,
    width: 20,
    marginRight: 8,
    color: theme.palette.icon.light,
  },

  loadingFade: {
    position: 'absolute',
    top: 94,
    left: 0,
    width: '100%',
    height: 'calc(100% - 154px)', // full height - header and footer size
    backgroundColor: theme.applyOpacityToHex?.(theme.palette.background.paper, 1) ?? theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 40,
    alignContent: 'center',
    color: theme.palette.text.secondary,
  },
  check: {
    color: theme.palette.primary.main,
    paddingRight: 10,
    paddingBottom: 5,
    transform: 'scale(1.5)',
  },
  brandingIcon: {
    border: `1px solid ${theme.palette.border.light}`,
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    borderRadius: 8,
    margin: '12px 24px',
    width: 64,
    height: 64,
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 72,
    flexShrink: 0,
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.text.secondary,
  },
  termButton: {
    padding: '8px 16px 8px 0',
    borderRadius: 8,
  },
  rotate: {
    transform: 'rotate(90deg)',
  },
  terms: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 28,
  },
}));

function makeRegex(regexString: string) {
  let pattern = regexString;
  let flags = '';

  // if it’s wrapped in slashes, pull pattern & flags out
  const slashMatch = regexString.match(/^\/(.+)\/([a-z]*)$/i);
  if (slashMatch) {
    pattern = slashMatch[1];
    flags = slashMatch[2];
  }
  return new RegExp(pattern, flags);
}

interface LoginProps {
  enrollment?: EnrollResponse | null;
  biller?: Biller | null;
  setSteps: (data: any) => void;
  GUID: string;
  series: scheduledTransactionsTypes.ScheduledTransactionType;
}

const SubmittingOverlay = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.loadingFade}>
      <CircularProgress size={64} />
      <div className={classes.row}>
        <CheckIcon className={classes.check} />
        <Typography variant='paragraphRegular'>Connecting to Biller</Typography>
      </div>
    </div>
  );
};

interface FormField extends Field {
  value: string;
}
interface FormParam extends Parameter {
  fields: FormField[];
}

function initializeForm(enrollment?: EnrollResponse | null) {
  if (!enrollment) return [];
  return enrollment.parameters.map((param: Parameter) => ({
    ...param,
    fields: param.fields.map((field) => ({ ...field, value: '' })),
  }));
}

function getFieldError(field: FormField) {
  const { validation, value } = field;
  if (validation) {
    if (validation.required && !field.value) {
      return 'Field is required';
    }
    if ((validation.required || field.value) && validation.minLength && value.length < validation.minLength) {
      return `Must be at least ${validation.minLength} characters`;
    }
    if (validation.maxLength && value.length > validation.maxLength) {
      return `Must be less than ${validation.maxLength + 1} characters`;
    }
    if (validation.regex) {
      const reg = makeRegex(validation.regex);
      if (reg && !reg.exec(value)) {
        return 'Check format and try again';
      }
    }
  }
  return null;
}

function hasFormError(formValues: FormParam[]) {
  return formValues.some((param) => param.fields.some(getFieldError));
}

const LoginView = ({ enrollment, biller, setSteps, GUID, series }: LoginProps) => {
  const { classes } = useStyles();
  const { billersWithLogos } = useBillersWithLogos(biller ? [biller] : []);
  const dispatch = useAppDispatch();

  // console.log('biller', biller);
  // console.log('enrollment', enrollment);

  const [formValues, setFormValues] = useState<FormParam[]>(() => initializeForm(enrollment));
  const [validate, setValidate] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [showTerms, setShowTerms] = useState<boolean>();

  const handleFormSubmission = async () => {
    log.info('LoginView: handleFormSubmission', { formValues });
    if (hasFormError(formValues)) {
      setValidate(true);
    } else if (biller) {
      // valid payload, do enroll
      setSubmitting(true);
      const parameters = formValues.map((param) => ({
        name: param.name,
        fields: param.fields.map((field) => ({
          name: field.name,
          value: field.value,
        })),
      }));

      const dataToSend = {
        nickName: biller.name,
        reference: GUID,
        billerId: biller.id,
        data: {
          consent: {
            value: 'Yes',
          },
          printSuppression: {
            value: enrollment?.printSuppression?.type === 'Mandatory' ? 'Yes' : 'No',
          },
          parameters,
        },
      };

      const response = await billPresentmentApi.submit(dataToSend);
      log.debug('Submit response', response);
      if (response.status === 'SUBMITTED' && response.id) {
        // here
        log.debug('Submit successful!', response);
        const confirmResponse = await billPresentmentApi.confirm(response.id);
        log.debug('Confirm response', confirmResponse);
        if (confirmResponse.status === 'CONNECTED' || confirmResponse.status === 'PENDING') {
          log.debug('Confirm successful!', response);
          dispatch(
            scheduledTransactionsActions.updateScheduledTransaction(
              scheduledTransactionsTypes.mkScheduledTransaction({
                id: series?.id,
                presentmentAccountId: confirmResponse.id,
              }),
            ),
          );
        }
        // TODO: look at response status to decide whether to send them to the complete page or not
        setSteps(StepEnum.PENDING);
      }
    }
    setSubmitting(false);
  };

  const websiteLink = biller?.url;

  const billerLogo = billersWithLogos[0].logoKey;

  return (
    <>
      <Dump obj={biller} />
      <img alt='Bank Icon' className={classes.brandingIcon} src={billerLogo || ConnectFIs} />

      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant='titleMedium'>{biller?.name}</Typography>

          <Typography variant='paragraphRegular' noWrap>
            <a href={websiteLink} target='_blank' className={classes.link}>
              {websiteLink}
            </a>
          </Typography>
        </div>

        <Typography variant='bodyRegular' margin='12px 0'>
          Enter the following account details to connect.
        </Typography>

        <div className={classes.form}>
          <Dump obj={enrollment} />

          {submitting ? (
            <SubmittingOverlay />
          ) : (
            <>
              {formValues.map((parameter, pIndex) => {
                if (!parameter?.fields) return null;
                return (
                  <Fragment key={parameter.name}>
                    {parameter.fields.length > 1 ? (
                      <Typography variant='captionMedium' marginTop={2}>
                        {parameter.label}
                      </Typography>
                    ) : null}
                    {parameter.fields.map((field, fIndex) => {
                      const fieldError = validate && getFieldError(field);
                      return (
                        <TextField
                          key={field.name}
                          value={field.value}
                          label={field.label}
                          onChange={(e) => {
                            setFormValues((oldForm) =>
                              _.set([...oldForm], [pIndex, 'fields', fIndex, 'value'], e?.target?.value),
                            );
                          }}
                          fullWidth
                          margin='dense'
                          helperText={fieldError}
                          error={Boolean(fieldError)}
                        />
                      );
                    })}
                  </Fragment>
                );
              })}

              <Divider flexItem sx={{ marginY: 1 }} />
              <ButtonBase id='terms-button' onClick={() => setShowTerms(!showTerms)} className={classes.termButton}>
                <Typography variant='paragraphRegular' color='textSecondary'>
                  <ChevronIcon className={showTerms ? classes.rotate : ''} sx={{ marginRight: '4px' }} />
                  {`By connecting, you agree to your biller’s terms.`}
                </Typography>
              </ButtonBase>

              {showTerms ? (
                <div className={classes.terms}>
                  <Typography variant='paragraphMedium' color='textSecondary' marginBottom='2px'>
                    From {biller?.name}:
                  </Typography>
                  <Typography variant='captionRegular' color='textSecondary'>
                    {`You consent to ${biller?.name} sharing your billing information with this service in line with biller’s `}
                    {'terms'}
                  </Typography>
                  <></>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className={classes.formActions}>
          <Typography variant='paragraphRegular' className={classes.footer} component='div'>
            <LockIcon className={classes.subLock} />
            Secured, encrypted connections
          </Typography>

          <Button
            id='submitButton'
            type='submit'
            variant='contained'
            startIcon={<LinkIcon />}
            onClick={handleFormSubmission}
          >
            Connect
          </Button>
        </div>
      </div>
    </>
  );
};

export default LoginView;
